import { GEOFENCE_DIRECTIONS, MAX_CORRIDOR_METRES, MAX_MOVEMENT_MINUTES } from '@chukua/common';
import { Form, Input, InputNumber, Select, type FormInstance } from 'antd';
import type { ReactNode } from 'react';
import type { AlertRuleFormValues } from '../policies/alert-rule-form';
import { DeadlineFields } from './AlertRuleDeadlineFields';
import { MilestoneFarFields, MilestoneMissingFields } from './AlertRuleContradictionFields';
import { AlertRuleTamperFields } from './AlertRuleTamperFields';

const DIRECTION_LABELS = { enter: 'Enters the zone', exit: 'Leaves the zone' } as const;

interface MinutesFieldProps {
  name: keyof AlertRuleFormValues;
  label: string;
  extra?: ReactNode;
}

function MinutesField({ name, label, extra }: MinutesFieldProps) {
  return (
    <Form.Item
      name={name}
      label={label}
      extra={extra}
      rules={[{ required: true, type: 'number', min: 1, max: MAX_MOVEMENT_MINUTES, message: `Enter 1 to ${MAX_MOVEMENT_MINUTES} minutes` }]}
    >
      <InputNumber min={1} max={MAX_MOVEMENT_MINUTES} className="w-32" />
    </Form.Item>
  );
}

/** The types whose only threshold is one number of minutes. */
const SINGLE_MINUTES_FIELDS: Partial<Record<string, MinutesFieldProps>> = {
  tracker_silent: { name: 'silentMinutes', label: 'Silent for (minutes)' },
  stopped: {
    name: 'stoppedMinutes',
    label: 'Standing still for (minutes)',
    extra: "Only trucks on a trip that has left. A stop at one of the trip's planned stops or in a safe stop area is not flagged.",
  },
  idling: { name: 'idleMinutes', label: 'Engine running, not moving, for (minutes)', extra: 'Needs a tracker that reports ignition.' },
};

function SpeedLimitField() {
  return (
    <Form.Item
      name="limitKph"
      label="Speed limit (km/h)"
      rules={[{ required: true, type: 'number', min: 1, max: 300, message: 'Enter a limit up to 300 km/h' }]}
    >
      <InputNumber min={1} max={300} className="w-32" />
    </Form.Item>
  );
}

function GeofenceFields() {
  return (
    <>
      <Form.Item
        name="zoneKey"
        label="Geofence id"
        extra="The id your GPS provider uses for the geofence."
        rules={[{ required: true, whitespace: true, message: 'Enter the geofence id' }]}
      >
        <Input maxLength={200} />
      </Form.Item>
      <Form.Item name="direction" label="Fires when the truck" initialValue="exit">
        <Select options={GEOFENCE_DIRECTIONS.map((direction) => ({ value: direction, label: DIRECTION_LABELS[direction] }))} />
      </Form.Item>
    </>
  );
}

function OffRouteFields() {
  return (
    <>
      <Form.Item
        name="toleranceMeters"
        label="Distance from the planned route (metres)"
        extra="Only trips whose route was planned in full are checked."
        rules={[{ required: true, type: 'number', min: 10, max: MAX_CORRIDOR_METRES, message: `Enter 10 to ${MAX_CORRIDOR_METRES} metres` }]}
      >
        <InputNumber min={10} max={MAX_CORRIDOR_METRES} className="w-32" />
      </Form.Item>
      <MinutesField name="minutes" label="Off route for (minutes)" />
    </>
  );
}

function UnsafeStopFields() {
  return (
    <>
      <MinutesField name="stoppedMinutes" label="Stopped in an unsafe area for (minutes)" extra="Areas are marked under Zones → Flagged areas." />
      <Form.Item
        name="overnightMinutes"
        label="Or stopped overnight for (minutes)"
        extra="Counts the time between 22:00 and 05:00 outside safe stop areas. Leave empty to check unsafe areas only."
        rules={[{ type: 'number', min: 1, max: MAX_MOVEMENT_MINUTES, message: `Enter 1 to ${MAX_MOVEMENT_MINUTES} minutes` }]}
      >
        <InputNumber min={1} max={MAX_MOVEMENT_MINUTES} className="w-32" />
      </Form.Item>
    </>
  );
}

function FuelDropFields() {
  return (
    <>
      <Form.Item
        name="dropLitres"
        label="Fuel falls by at least (litres)"
        extra="Only trucks with a fuel sensor are watched. A refill recorded nearby (tracker, fuel stop or fuel purchase) is not a drop."
        rules={[{ required: true, type: 'number', min: 1, max: 2000, message: 'Enter litres, 1 to 2000' }]}
      >
        <InputNumber min={1} max={2000} className="w-32" />
      </Form.Item>
      <Form.Item
        name="withinMinutes"
        label="Within (minutes)"
        rules={[{ required: true, type: 'number', min: 1, max: 1440, message: 'Enter minutes, 1 to 1440' }]}
      >
        <InputNumber min={1} max={1440} className="w-32" />
      </Form.Item>
    </>
  );
}

/** The types whose fields need nothing from the form. */
const STANDALONE_FIELDS: Partial<Record<string, () => ReactNode>> = {
  geofence: GeofenceFields,
  off_route: OffRouteFields,
  unsafe_stop: UnsafeStopFields,
  fuel_drop: FuelDropFields,
  milestone_missing: MilestoneMissingFields,
  milestone_far: MilestoneFarFields,
  delivery_deadline: DeadlineFields,
  empty_return_deadline: DeadlineFields,
};

/** The fields that depend on the rule's type: how long silent, which geofence and crossing, what speed, how long a movement lasts, how far the tank falls, or which tamper causes. */
export function AlertRuleThresholdFields({ form }: { form: FormInstance<AlertRuleFormValues> }) {
  const type = Form.useWatch('type', form);

  const minutes = type ? SINGLE_MINUTES_FIELDS[type] : undefined;
  if (minutes) return <MinutesField {...minutes} />;
  const Standalone = type ? STANDALONE_FIELDS[type] : undefined;
  if (Standalone) return <Standalone />;

  switch (type) {
    case 'speeding_sustained':
      return (
        <>
          <SpeedLimitField />
          <MinutesField name="minutes" label="Over the limit for (minutes)" extra="One fast reading does not fire it; the truck has to stay over." />
        </>
      );
    case 'tracker_tamper':
      return <AlertRuleTamperFields form={form} />;
    default:
      return <SpeedLimitField />;
  }
}
